/**
 * MTC Deal — shared JS for deal add-to-cart functionality.
 * Used on PDP (mtc-product-deals block) and collection cards (mtc-deal-card snippet).
 */

/**
 * Add a deal bundle to the cart from the PDP deal box.
 * Multiplies by the quantity selector value (how many deal sets the customer wants).
 *
 * @param {HTMLButtonElement} button - The button that was clicked
 * @param {number} mainVariantId - Variant ID of the main product (item A)
 * @param {number} giftVariantId - Variant ID of the deal product (item B)
 * @param {number} [buysMin=1] - How many of item A per deal unit
 * @param {number} [getsNum=1] - How many of item B per deal unit
 */
function addBundleToCart(button, mainVariantId, giftVariantId, buysMin = 1, getsNum = 1) {
  // Find quantity from the neighbor quantity selector or default to main deal box
  let quantityInput = button.closest('.price-box')?.querySelector('input[name="quantity"]');
  if (!quantityInput) {
    quantityInput = document.querySelector('.price-box__deal input[name="quantity"]');
  }
  const parsedQty = parseInt(quantityInput?.value, 10);
  const quantity = Number.isFinite(parsedQty) && parsedQty > 0 ? parsedQty : 1;

  const items = [
    {
      id: mainVariantId,
      quantity: quantity * buysMin,
    },
  ];

  if (getsNum > 0) {
    items.push({
      id: giftVariantId,
      quantity: quantity * getsNum,
    });
  }

  _mtcDealAddToCart(button, items);
}

/**
 * Add a deal to the cart from a collection product card.
 * Adds exactly one deal set (no quantity selector on cards).
 *
 * @param {HTMLButtonElement} button - The button that was clicked
 * @param {number} mainVariantId - Variant ID of the main product (item A)
 * @param {number} dealVariantId - Variant ID of the deal product (item B)
 * @param {number} [buysMin=1] - How many of item A per deal unit
 * @param {number} [getsNum=0] - How many of item B per deal unit
 */
function addDealToCart(button, mainVariantId, dealVariantId, buysMin = 1, getsNum = 0) {
  const items = [
    { id: mainVariantId, quantity: buysMin },
  ];

  if (getsNum > 0) {
    items.push({ id: dealVariantId, quantity: getsNum });
  }

  _mtcDealAddToCart(button, items);
}

/**
 * Auto-add deal items when either the standard Add to Cart form or a cart quantity
 * change results in the main product meeting the deal threshold.
 *
 * Unified approach: listen for any cart:update, compare the total main product
 * quantity in the cart against how many deal items are already there, and top up
 * the difference. Works for both:
 *  - PDP add-to-cart (CartAddEvent fires with full cart in detail.resource)
 *  - Cart drawer quantity increase (CartUpdateEvent fires with full cart in detail.resource)
 */
(function initDealInterceptor() {
  const dealConfig = document.querySelector('.mtc-deal-config');
  if (!dealConfig) return;

  const mainProductId = parseInt(dealConfig.dataset.productId, 10);
  const buysMin = parseInt(dealConfig.dataset.buysMin, 10);
  const getsNum = parseInt(dealConfig.dataset.getsNum, 10);
  const dealVariantId = parseInt(dealConfig.dataset.dealVariantId, 10);

  if (!mainProductId || !buysMin || !getsNum || !dealVariantId) return;

  let dealAddInProgress = false;
  let lastMainQty = null; // null until the baseline cart is loaded

  const computeMainQty = (cart) =>
    cart.items.filter((item) => item.product_id === mainProductId).reduce((sum, item) => sum + item.quantity, 0);

  // Establish the baseline from the authoritative cart so an existing cart
  // (or a cart:update that fires before any user action) never triggers an add.
  _fetchCart().then((cart) => {
    lastMainQty = cart?.items ? computeMainQty(cart) : 0;
  });

  document.addEventListener('cart:update', async (event) => {
    if (dealAddInProgress) return;

    const isOwnEvent = event.detail?.sourceId === 'mtc-deal-add-button';

    // Work from the full cart. Horizon's CartAddEvent/CartUpdateEvent carry it in
    // detail.resource, but our own cart/add response is partial, so fetch in that case.
    const cart =
      event.detail?.resource?.items && !isOwnEvent ? event.detail.resource : await _fetchCart();
    if (!cart?.items) return;

    const mainQty = computeMainQty(cart);
    const prevMainQty = lastMainQty;
    lastMainQty = mainQty; // always keep the baseline in sync

    // Only top the gift up when the qualifying quantity INCREASES. This stops the
    // interceptor from re-adding a gift the customer deliberately removed (main
    // unchanged) or reduced. Skip until the baseline is known and on our own re-adds.
    if (prevMainQty === null || isOwnEvent || mainQty <= prevMainQty) return;

    const giftsToAdd =
      Math.floor(mainQty / buysMin) * getsNum - Math.floor(prevMainQty / buysMin) * getsNum;
    if (giftsToAdd <= 0) return;

    dealAddInProgress = true;

    try {
      await _mtcDealCartAdd([{ id: dealVariantId, quantity: giftsToAdd }]);
    } catch (error) {
      console.error('Deal item add failed:', error);
    } finally {
      dealAddInProgress = false;
    }
  });

  async function _fetchCart() {
    try {
      const r = await fetch(window.Shopify.routes.root + 'cart.js');
      return await r.json();
    } catch {
      return null;
    }
  }
})();

/**
 * Internal: POST items to cart/add.js and dispatch the cart:update event.
 * Shared by both the manual deal buttons (_mtcDealAddToCart) and the
 * auto-add interceptor so the request/dispatch logic lives in one place.
 *
 * @param {Array} items - Array of {id, quantity} objects
 * @returns {Promise<Object>} The parsed cart/add.js response
 * @throws {Error} On a failed add; the parsed response is attached as err.data
 */
async function _mtcDealCartAdd(items) {
  // Find all cart sections to update (drawer, page, etc.)
  const sectionsToUpdate = [];
  document.querySelectorAll('cart-items-component').forEach((component) => {
    if (component.dataset.sectionId) {
      sectionsToUpdate.push(component.dataset.sectionId);
    }
  });

  const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: items,
      sections: sectionsToUpdate.join(','),
      sections_url: window.location.pathname,
    }),
  });
  const data = await response.json();

  if (!response.ok) {
    const err = new Error(data.description || 'Cart add failed');
    err.data = data;
    throw err;
  }

  // Dispatch update event for theme to handle cart drawer / header count
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: data,
        sourceId: 'mtc-deal-add-button',
        data: {
          sections: data.sections,
          itemCount: data.item_count,
        },
      },
    })
  );

  return data;
}

/**
 * Add deal items from a manual button: shows loading state, opens the cart
 * drawer on success, alerts on failure.
 *
 * @param {HTMLButtonElement} button - The clicked button (for loading state)
 * @param {Array} items - Array of {id, quantity} objects
 */
function _mtcDealAddToCart(button, items) {
  button.classList.add('loading');
  button.disabled = true;

  _mtcDealCartAdd(items)
    .then(() => {
      // Open cart drawer if available
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && typeof cartDrawer.showDialog === 'function') {
        cartDrawer.showDialog();
      }
    })
    .catch((error) => {
      console.error('Error adding deal to cart:', error);
      if (error.data?.description) alert(error.data.description);
    })
    .finally(() => {
      button.classList.remove('loading');
      button.disabled = false;
    });
}
